import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { apiClient } from '../api/client';
import NeonCrowLogo from '../components/NeonCrowLogo';

export default function LoginPage() {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const { login } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await apiClient.login(formData.username, formData.password);
      login(response);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen flex items-start justify-center bg-[#0b0b12] pt-4 sm:pt-8 pb-8 sm:pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-4 sm:space-y-6">
        <div className="text-center">
          <div className="mb-4">
            <NeonCrowLogo size={80} className="sm:hidden" />
            <NeonCrowLogo size={120} className="hidden sm:block" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white">
            Welcome to Neon Crow Games
          </h2>
          <p className="mt-2 text-sm text-zinc-400">
            Sign in to your account or{' '}
            <Link
              to="/register"
              className="font-medium text-cyan-400 hover:text-cyan-300 transition-colors touch-target"
            >
              create a new account
            </Link>
          </p>
        </div>
        <form className="mt-4 sm:mt-6 space-y-4 sm:space-y-5" onSubmit={handleSubmit}>
          {error && (
            <div style={{
              background: "rgba(239, 68, 68, 0.1)",
              border: "2px solid rgba(239, 68, 68, 0.3)",
              borderRadius: "8px",
              padding: "16px",
              display: "flex",
              alignItems: "start",
              gap: "12px"
            }}>
              <svg 
                style={{ width: "24px", height: "24px", flexShrink: 0, marginTop: "2px" }}
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                  style={{ color: "#ef4444" }}
                />
              </svg>
              <div>
                <div style={{
                  color: "#ef4444",
                  fontWeight: 600,
                  fontSize: "16px",
                  marginBottom: "4px"
                }}>
                  Login Failed
                </div>
                <div style={{
                  color: "#fca5a5",
                  fontSize: "14px",
                  lineHeight: 1.5
                }}>
                  {error}
                </div>
              </div>
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-zinc-300 mb-2">
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                value={formData.username}
                onChange={handleChange}
                className="input-field"
                placeholder="Enter your username"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-zinc-300 mb-2">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="input-field"
                placeholder="Enter your password"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full"
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
